// The cards Policy chat puts in the chat transcript: the change picker, one
// card per step, data views, the settings review, the pilot, and the result.
// Each card reads the session store, so a click here and an answer the model
// records show the same state. A card for a step that is not current shows
// its answer only.
//
// The cards sit in the chat column, often under 600px wide, so every list is a
// column of rows (a name with a line of detail, and its controls at the right)
// rather than a table, and the controls use the chat's own card buttons.
import { openAgentPolicyPilot } from "../../../lib/agentPolicyNavigation.js";
import { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useTraceCanvas, useTraceCanvasOpen } from "../../../lib/traceCanvasStore.js";
import { hasPermission, useMyPermissions } from "../../../lib/permissions.js";
import { PERMISSIONS } from "../../../lib/permissionKeys.js";
import { getTriageChatHost } from "../../../lib/triageChatStore.js";
import { askDockTab } from "../../../lib/dockBridge.js";
import Button from "../../../components/ui/Button.jsx";
import AnchoredPopover from "../../../components/AnchoredPopover.jsx";
import { CARD, CARD_BUTTON, CARD_MUTED, CARD_PICKED } from "../../../components/ChatQuestionCard.jsx";
import { FILE_LABEL } from "../../../components/lenses/triageParts.jsx";
import { valueAt } from "../catalogDocument.js";
import { useEndpointSearch } from "../ChoosePilotEndpoints.jsx";
import { AGENT_IDS, CHANGES, DECISION_LABELS, RULE_KINDS, RULE_LIMITS, aboutForPeople, agentRulesText, featureOffToday } from "./changes.js";
import { count, keyName, labelOf, rowMeta, show } from "./cardText.js";
import McpServerGraph from "./McpServerGraph.jsx";
import {
    changeOf, changedAgents, changesFor, chooseChange, confirmStep, draftAnswer, getSession, inScope, loadServerEndpoints,
    restoreSession, parseCardKey, planSteps, proposalReviewAvailable, missingProposalSteps, pilotAgentLocked, pilotResultFor, remainingPilotAgents, showCurrentProposal, reopenStep, rowsFor, setPilot, startPilots, stepById, subscribeSession, summarize, toPilot, warnings,
} from "./session.js";
import { SOURCES } from "./sources.js";

const PILOT_LIMIT = 50;
const CompactProposal = createContext(false);
const LIVE = "border-[color:var(--line-strong)]";
const QUESTION = "text-[13px] leading-[1.4] text-[color:var(--ink)]";
const ROWS = "grid";
const ROW = "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-[10px] border-0 border-t border-solid border-[color:var(--line-soft)] px-[4px] py-[6px]";
const NAME = "min-w-0 break-words text-[12px] leading-[1.35] text-[color:var(--ink)]";
const META = "block text-[11px] leading-[1.35] text-[color:var(--muted)]";
const MONO = "font-mono text-[11px]";
const BTN = `${CARD_BUTTON} whitespace-nowrap`;
const DANGER_ON = "border-[#a2401c] !bg-[#a2401c]/15 text-[color:var(--ink)]";
const INPUT = "min-h-[24px] w-full min-w-0 rounded-[2px] border border-[color:var(--line)] bg-transparent px-[7px] text-[11px] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]";
const TICK = "✓";


function useSession(sessionId) {
    useSyncExternalStore(
        (fn) => subscribeSession(sessionId, fn),
        () => getSession(sessionId)?.version ?? -1,
        () => -1,
    );
    return getSession(sessionId);
}

/// The page posts a card without a model turn, and the card before it often
/// shrinks to its summary in the same render, so the transcript no longer
/// counts the reader as at its end and does not follow. A card that takes
/// answers scrolls the transcript to its end once, after layout.
function useShowWhenLive(live) {
    const ref = useRef(null);
    const shown = useRef(false);
    useEffect(() => {
        if (!live || shown.current) return undefined;
        shown.current = true;
        const frame = requestAnimationFrame(() => {
            const viewport = ref.current?.closest("[data-overlayscrollbars-viewport], .os-viewport");
            if (viewport) viewport.scrollTop = viewport.scrollHeight;
        });
        return () => cancelAnimationFrame(frame);
    }, [live]);
    return ref;
}

function Card({ live = false, label, question, children, cardRef = null }) {
    const compact = useContext(CompactProposal);
    return (
        <div ref={cardRef} className={`${CARD} ${live ? LIVE : ""}`}>
            {label && !compact ? <span className={FILE_LABEL}>{label}</span> : null}
            {label && compact && !question ? <strong className="text-[13px] font-medium">{label}</strong> : null}
            {question ? <span className={QUESTION}>{question}</span> : null}
            {children}
        </div>
    );
}

function Segments({ options, value, onPick, disabled }) {
    return (
        <span className="inline-flex gap-[4px]">
            {options.map((o) => (
                <button key={o.id} type="button" aria-pressed={value === o.id} disabled={disabled} onClick={() => onPick(o.id)} className={`${BTN} ${value === o.id ? (o.danger ? DANGER_ON : CARD_PICKED) : ""}`}>
                    {o.label}
                </button>
            ))}
        </span>
    );
}

function Notes({ items }) {
    if (!items.length) return null;
    return (
        <div className="grid gap-[4px]">
            {items.map((w) => (
                <p key={w.text} className={`m-0 border-0 border-l-2 border-solid px-[8px] py-[3px] text-[11px] leading-[1.4] text-[color:var(--ink)] ${w.tone === "info" ? "border-[color:var(--line-strong)]" : "border-[#aa742f]"}`}>{w.text}</p>
            ))}
        </div>
    );
}

function Footer({ children, hint = "Or answer, add a note, or ask a question below." }) {
    return (
        <span className="flex flex-wrap items-center gap-[8px] border-0 border-t border-solid border-[color:var(--line-soft)] pt-[6px]">
            {children}
            {hint ? <span className={`${CARD_MUTED} ml-auto`}>{hint}</span> : null}
        </span>
    );
}

function supportText(change) {
    return AGENT_IDS.map((a) => {
        const level = change.support[a].level;
        return level === "yes" ? labelOf(a) : level === "partly" ? `${labelOf(a)} (partly)` : null;
    }).filter(Boolean).join(", ");
}

export function PickCard({ session }) {
    const chosen = changeOf(session);
    const live = !chosen && !session.ended && session.canManage;
    return (
        <Card live={live} label={chosen ? "Change" : "Change · pick one"} question="What do you want to change?">
            <span className={ROWS}>
                {CHANGES.map((change, i) => (
                    <button
                        key={change.id}
                        type="button"
                        aria-pressed={chosen?.id === change.id}
                        disabled={!live}
                        onClick={() => chooseChange(session.id, change.id)}
                        className={`${ROW} cursor-pointer bg-transparent text-left hover:bg-[color:var(--control-bg)] disabled:cursor-default ${chosen?.id === change.id ? "!bg-[color:var(--control-bg)]" : ""}`}
                    >
                        <span className={NAME}>
                            <span className="mr-[8px] font-mono text-[10px] text-[color:var(--muted-soft)]">{i + 1}</span>
                            {change.title}
                            <span className={`${META} pl-[18px]`}>{supportText(change)}</span>
                        </span>
                        {chosen?.id === change.id ? <span aria-hidden="true" className="text-[11px] text-[color:var(--ink)]">{TICK}</span> : <span />}
                    </button>
                ))}
            </span>
        </Card>
    );
}

function AgentsBody({ session, live }) {
    const change = changeOf(session);
    return (
        <>
            <span className="flex flex-wrap gap-[6px]">
                {AGENT_IDS.filter((a) => change.support[a].level !== "no").map((a) => {
                    const on = inScope(session, a);
                    return <button key={a} type="button" aria-pressed={on} disabled={!live} onClick={() => draftAnswer(session.id, "agents", { [a]: !on })} className={`${BTN} ${on ? CARD_PICKED : ""}`}>{on ? `${TICK} ` : ""}{labelOf(a)}</button>;
                })}
            </span>
            {session.context ? <span className={CARD_MUTED}>{session.context.attribution}</span> : null}
            {AGENT_IDS.filter((a) => change.support[a].level !== "yes").map((a) => <span key={a} className={CARD_MUTED}>{`${labelOf(a)}: ${change.support[a].why}`}</span>)}
        </>
    );
}

function ChoiceBody({ session, step, live }) {
    const value = session.answers[step.id];
    return (
        <span className={ROWS} role="radiogroup">
            {step.options.map((o) => (
                <button key={o.id} type="button" role="radio" aria-checked={value === o.id} disabled={!live} onClick={() => { draftAnswer(session.id, step.id, o.id); confirmStep(session.id, step.id); }} className={`${ROW} cursor-pointer bg-transparent text-left hover:bg-[color:var(--control-bg)] disabled:cursor-default ${value === o.id ? "!bg-[color:var(--control-bg)]" : ""}`}>
                    <span className={NAME}>
                        {o.label}{o.recommended ? <span className="ml-[6px] text-[10px] tracking-[0.06em] text-[color:var(--green)] uppercase">Recommended</span> : null}
                        {o.detail ? <span className={META}>{o.detail}</span> : null}
                        {o.perAgent ? AGENT_IDS.filter((a) => o.perAgent[a] && inScope(session, a)).map((a) => <span key={a} className={META}>{`${labelOf(a)}: ${o.perAgent[a]}`}</span>) : null}
                    </span>
                    {value === o.id ? <span aria-hidden="true" className="text-[11px] text-[color:var(--ink)]">{TICK}</span> : <span />}
                </button>
            ))}
        </span>
    );
}

function ToggleRows({ options, picked, onToggle, live }) {
    return (
        <span className={ROWS}>
            {options.map((o) => {
                const on = picked(o.id);
                return (
                    <button key={o.id} type="button" role="checkbox" aria-checked={on} disabled={!live} onClick={() => onToggle(o.id)} className={`${ROW} cursor-pointer bg-transparent text-left hover:bg-[color:var(--control-bg)] disabled:cursor-default ${on ? "!bg-[color:var(--control-bg)]" : ""}`}>
                        <span className={NAME}>{o.label}{o.detail ? <span className={META}>{o.detail}</span> : null}</span>
                        {on ? <span aria-hidden="true" className="text-[11px] text-[color:var(--ink)]">{TICK}</span> : <span />}
                    </button>
                );
            })}
        </span>
    );
}

function CheckBody({ session, step, live }) {
    const on = session.answers[step.id] === true;
    return <ToggleRows live={live} options={[{ id: "yes", label: "Yes", detail: step.help }]} picked={() => on} onToggle={() => draftAnswer(session.id, step.id, !on)} />;
}

const ON_OFF = [{ id: "on", label: "On" }, { id: "off", label: "Off", danger: true }];

const HELP = "ml-[6px] inline-grid h-[14px] w-[14px] cursor-pointer place-items-center rounded-full border border-solid border-[color:var(--line-strong)] bg-transparent p-0 align-[1px] text-[9px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)] aria-expanded:text-[color:var(--ink)]";

/// What the feature does and whether it is off today, and behind a question
/// mark what turning it off does in this agent.
function FeatureName({ session, row, agentId }) {
    const [open, setOpen] = useState(false);
    const anchor = useRef(null);
    const off = featureOffToday(session.today?.[agentId]?.document, row, agentId);
    return (
        <span className={NAME}>
            {row.label}
            <button ref={anchor} type="button" aria-expanded={open} aria-label={`Turning off ${row.label.toLowerCase()}`} className={HELP} onClick={() => setOpen(!open)}>?</button>
            {open ? (
                // Above the chat's floating overlay (z-index 10000 in ChatConsole).
                <AnchoredPopover anchor={anchor} onClose={() => setOpen(false)} label={`Turning off ${row.label.toLowerCase()}`} className="!z-[10010] max-w-[360px] px-[12px] py-[10px] text-[11px] leading-[1.4] text-[color:var(--muted)]">
                    {aboutForPeople(row.about[agentId])}
                </AnchoredPopover>
            ) : null}
            <span className={META}>{row.what}</span>
            <span className={`${META} !text-[color:var(--muted-soft)]`}>{off ? "Off today." : "On today."}</span>
        </span>
    );
}

function FeaturesBody({ session, step, live }) {
    const value = session.answers[step.id] ?? {};
    return (
        <span className={ROWS}>
            {step.rows.map((r) => (
                <span key={r.id} className={ROW}>
                    <FeatureName session={session} row={r} agentId={step.scope} />
                    <Segments options={ON_OFF} value={value[r.id] ?? null} disabled={!live} onPick={(v) => draftAnswer(session.id, step.id, { ...value, [r.id]: v })} />
                </span>
            ))}
        </span>
    );
}

const VIEWS = [{ id: "graph", label: "Graph" }, { id: "list", label: "List" }];

function DecisionsBody({ session, step, live }) {
    const [term, setTerm] = useState("");
    const [added, setAdded] = useState("");
    const [view, setView] = useState("graph");
    const [selectedId, setSelectedId] = useState(null);
    useEffect(() => {
        if (selectedId && step.source === "mcp_servers") loadServerEndpoints(session.id, selectedId);
    }, [selectedId, session.id, step.source]);
    const value = session.answers[step.id] ?? {};
    const state = step.source ? session.rowsState[step.source] : "ready";
    const rows = rowsFor(session, step);
    const extra = Object.keys(value).filter((k) => !rows.some((r) => r.id === k)).map((k) => ({ id: k, name: k, extra: true }));
    const listed = [...rows, ...extra].filter((r) => !term.trim() || r.name.toLowerCase().includes(term.trim().toLowerCase()));
    const set = (id, decision) => {
        const next = { ...value };
        if (next[id] === decision) delete next[id];
        else next[id] = decision;
        draftAnswer(session.id, step.id, next);
    };
    const graph = step.source === "mcp_servers" && rows.length > 0 && view === "graph";
    return (
        <span className="grid gap-[6px]" aria-busy={state === "loading" ? "true" : "false"}>
            {state === "error" ? <span className={CARD_MUTED}>The activity could not be read. You can still add entries by name.</span> : null}
            {step.source === "mcp_servers" && rows.length ? <span><Segments options={VIEWS} value={view} onPick={setView} /></span> : null}
            {graph ? (
                <McpServerGraph
                    rows={rows}
                    isBlocked={(id) => value[id] === "block"}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    endpoints={selectedId ? session.serverEndpoints?.[selectedId] ?? null : null}
                    renderControl={(r) => <Segments options={step.decisions} value={value[r.id] ?? null} disabled={!live} onPick={(d) => set(r.id, d)} />}
                />
            ) : null}
            {graph && extra.length ? <span className={CARD_MUTED}>{`Added by name, not in the graph: ${extra.map((r) => r.name).join(", ")}.`}</span> : null}
            {!graph && rows.length > 8 ? <input className={INPUT} placeholder="Search" value={term} onChange={(e) => setTerm(e.target.value)} /> : null}
            {graph ? null : <span className={`${ROWS} max-h-[360px] overflow-y-auto`}>
                {listed.map((r) => (
                    <span key={r.id} className={ROW}>
                        <span className={NAME}>
                            <span className={MONO}>{r.name}</span>
                            <span className={META}>{r.extra ? "Added by name" : rowMeta(r)}</span>
                        </span>
                        <Segments options={step.decisions} value={value[r.id] ?? null} disabled={!live} onPick={(d) => set(r.id, d)} />
                    </span>
                ))}
                {!listed.length && term ? <span className={CARD_MUTED}>{`No match for "${term}".`}</span> : null}
            </span>}
            {live && step.addByName ? (
                <form className="flex gap-[6px]" onSubmit={(e) => { e.preventDefault(); const v = added.trim(); if (!v) return; set(v, step.decisions[step.decisions.length - 1].id); setAdded(""); }}>
                    <input className={INPUT} placeholder={step.addByName} value={added} onChange={(e) => setAdded(e.target.value)} />
                    <button type="submit" className={BTN}>Add</button>
                </form>
            ) : null}
        </span>
    );
}

const KIND_OPTIONS = Object.entries(RULE_KINDS).map(([id, k]) => ({ id, label: k.label }));
const decisionOptions = (ids) => ids.map((id) => ({ id, label: DECISION_LABELS[id], danger: id === "deny" }));

/// Each agent's rules for one decision on a set of entries.
function AgentRules({ entries, decision }) {
    return (
        <span className="grid gap-[2px]">
            {agentRulesText(entries, decision).map((r) => (
                <span key={r.agentId} className={`${META} break-words`}><span className="text-[color:var(--ink)]">{`${labelOf(r.agentId)}: `}</span><span className={MONO}>{r.text}</span></span>
            ))}
        </span>
    );
}

function RuleRow({ entry, open, onOpen, live, onChange, onRemove }) {
    const spec = RULE_KINDS[entry.kind] ?? RULE_KINDS.command;
    const removed = entry.decision === null;
    const fixed = !live || !!entry.today;
    const target = String(entry.target ?? "");
    return (
        <span className="grid gap-[4px] border-0 border-t border-solid border-[color:var(--line-soft)] px-[4px] py-[6px]">
            <span className="flex flex-wrap items-center gap-[6px]">
                <select aria-label="Kind" className={`${INPUT} !w-auto`} value={entry.kind} disabled={fixed} onChange={(e) => onChange({ kind: e.target.value, decision: RULE_KINDS[e.target.value].decisions.includes(entry.decision) ? entry.decision : "ask" })}>
                    {KIND_OPTIONS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
                <input aria-label={spec.label} className={`${INPUT} ${MONO} !w-auto min-w-[120px] flex-1`} placeholder={spec.placeholder} value={target} readOnly={fixed} onFocus={onOpen} onChange={(e) => onChange({ target: e.target.value })} />
                {removed ? null : <Segments options={decisionOptions(spec.decisions)} value={entry.decision} disabled={!live} onPick={(d) => { onOpen(); onChange({ decision: d }); }} />}
                {live ? <button type="button" className={BTN} onClick={onRemove}>{removed ? "Undo" : "Remove"}</button> : null}
            </span>
            {removed ? <span className={META}>Removed. Its rules leave today's policies.</span> : null}
            {open && !removed && target.trim() ? (
                <span className="grid gap-[4px] pl-[2px]">
                    {RULE_LIMITS[entry.kind] ? <span className={META}>{RULE_LIMITS[entry.kind]}</span> : null}
                    {entry.kind === "command" && !target.includes("*") ? (
                        <label className={`${META} flex items-center gap-[6px]`}>
                            <input type="checkbox" checked={!!entry.fullPaths} disabled={!live} onChange={() => onChange({ fullPaths: !entry.fullPaths })} />
                            {`Also match ${target.trim().split(/\s+/)[0]} run by its full path`}
                        </label>
                    ) : null}
                    <AgentRules entries={[entry]} decision={entry.decision} />
                </span>
            ) : null}
        </span>
    );
}

function RulesBody({ session, step, live }) {
    const rules = session.answers[step.id] ?? [];
    const [open, setOpen] = useState(0);
    const write = (next) => draftAnswer(session.id, step.id, next);
    const change = (i, patch) => write(rules.map((e, j) => (j === i ? { ...e, ...patch } : e)));
    const remove = (i) => {
        const e = rules[i];
        if (!e.today) write(rules.filter((_, j) => j !== i));
        else change(i, { decision: e.decision === null ? e.todayDecision ?? "ask" : null });
    };
    return (
        <span className="grid gap-[6px]">
            {rules.length ? (
                <span className={ROWS}>
                    {rules.map((e, i) => (
                        <RuleRow key={e.today ? `today:${e.target}` : `new:${i}`} entry={e} open={open === i} onOpen={() => setOpen(i)} live={live} onChange={(patch) => change(i, patch)} onRemove={() => remove(i)} />
                    ))}
                </span>
            ) : null}
            {live ? (
                <span className="flex flex-wrap items-center gap-[8px]">
                    <button type="button" className={BTN} onClick={() => { write([...rules, { kind: "command", target: "", decision: "ask" }]); setOpen(rules.length); }}>Add a rule</button>
                    <span className={CARD_MUTED}>Or describe it below, for example: keep agents out of ~/.aws</span>
                </span>
            ) : null}
        </span>
    );
}

function ThemeRow({ row, decision, decisions, open, onToggle, live, onPick }) {
    const suggested = decisions.find((d) => d.id === row.suggest)?.label;
    return (
        <span className="grid gap-[4px] border-0 border-t border-solid border-[color:var(--line-soft)] px-[4px] py-[6px]">
            <span className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-[10px]">
                <button type="button" aria-expanded={open} onClick={onToggle} className={`${NAME} cursor-pointer border-0 bg-transparent p-0 text-left`}>
                    {row.name}
                    <span className={META}>{`${count(row.cases, "case", "cases")}.${suggested ? ` Suggested: ${suggested}.` : ""}`}</span>
                    <span className={`${META} ${MONO} break-words`}>{row.rules.map((r) => r.target).join(", ")}</span>
                </button>
                <Segments options={decisions} value={decision ?? null} disabled={!live} onPick={onPick} />
            </span>
            {open ? (
                <span className="grid gap-[4px] pl-[2px]">
                    <span className={FILE_LABEL}>Cases</span>
                    {row.titles.map((t) => <span key={t} className={META}>{t}</span>)}
                    {row.cases > row.titles.length ? <span className={META}>{`And ${count(row.cases - row.titles.length, "more case", "more cases")} in Triage.`}</span> : null}
                    {row.note ? <span className={META}>{row.note}</span> : null}
                    <span className={FILE_LABEL}>Each agent gets</span>
                    <AgentRules entries={row.rules} decision={decision ?? row.suggest} />
                </span>
            ) : null}
        </span>
    );
}

function ThemesBody({ session, step, live }) {
    const [open, setOpen] = useState(null);
    const value = session.answers[step.id] ?? {};
    const state = session.rowsState[step.source];
    const rows = rowsFor(session, step);
    const groups = rows.filter((r) => !r.noRule);
    const uncovered = rows.find((r) => r.noRule);
    const set = (id, decision) => {
        const next = { ...value };
        if (next[id] === decision) delete next[id];
        else next[id] = decision;
        draftAnswer(session.id, step.id, next);
    };
    const empty = state === "error" || (state === "ready" && !rows.length);
    return (
        <span className="grid gap-[6px]" aria-busy={state === "loading" ? "true" : "false"}>
            {state === "error" ? <span className={CARD_MUTED}>The Triage cases could not be read.</span> : null}
            {state === "ready" && !rows.length ? <span className={CARD_MUTED}>No cited command or tool evidence was available for this selection.</span> : null}
            {empty && live ? <span><button type="button" className={BTN} onClick={() => reopenStep(session.id, "known")}>Name the tool calls instead</button></span> : null}
            {rows.length ? (
                <span className="flex flex-wrap items-center gap-[8px]">
                    <span className={META}>{session.context ? `${count(rows[0].total, "finding", "findings")} from this investigation. A finding can be in more than one group.` : `${count(rows[0].total, "Triage case", "Triage cases")} in the last 30 days. A case can be in more than one group.`}</span>
                    {live && groups.length ? <button type="button" className={`${BTN} ml-auto`} onClick={() => draftAnswer(session.id, step.id, { ...value, ...Object.fromEntries(groups.map((r) => [r.id, r.suggest])) })}>Use suggested</button> : null}
                </span>
            ) : null}
            <span className={ROWS}>
                {groups.map((r) => (
                    <ThemeRow key={r.id} row={r} decision={value[r.id]} decisions={step.decisions} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} live={live} onPick={(d) => set(r.id, d)} />
                ))}
                {uncovered ? (
                    <span className={`${ROW} grid-cols-1`}>
                        <span className={NAME}>
                            {uncovered.name}
                            <span className={META}>{`${count(uncovered.cases, "case cites", "cases cite")} no tool call that a command, file, or MCP tool rule covers, such as a secret written into a file.`}</span>
                            {uncovered.titles.map((t) => <span key={t} className={META}>{t}</span>)}
                        </span>
                    </span>
                ) : null}
            </span>
        </span>
    );
}

function TextBody({ session, step, live }) {
    return <textarea aria-label={step.question} className={INPUT} rows={6} maxLength={4000} disabled={!live} value={session.answers[step.id] || ""} onChange={(e) => draftAnswer(session.id, step.id, e.target.value)} />;
}
const BODIES = { text: TextBody, agents: AgentsBody, choice: ChoiceBody, check: CheckBody, features: FeaturesBody, decisions: DecisionsBody, rules: RulesBody, themes: ThemesBody };

export function StepCard({ session, stepId, instance }) {
    const step = stepById(session, stepId);
    const latest = session.presented?.[stepId] === instance;
    const current = latest && session.cur === stepId;
    const live = current && session.canManage && !session.started && !session.ended;
    const ref = useShowWhenLive(current && !session.ended);
    if (!step) return null;
    const scope = step.scope === "all" ? "" : ` · ${labelOf(step.scope)} only`;
    if (!current) {
        return (
            <Card label={`Step${scope}${session.done.includes(stepId) ? " · answered" : ""}`} question={step.question}>
                <span className={CARD_MUTED}>{!planSteps(session).some((s) => s.id === stepId) ? "Not used. An earlier answer changed." : session.done.includes(stepId) ? summarize(session, step) : "Not answered"}</span>
            </Card>
        );
    }
    const Body = BODIES[step.card];
    return (
        <Card cardRef={ref} live={!session.ended} label={`Step${scope}`} question={step.question}>
            {step.help && step.card !== "check" ? <span className={CARD_MUTED}>{step.help}</span> : null}
            <Body session={session} step={step} live={live} />
            <Notes items={warnings(session)} />
            {session.ended ? <Ended /> : null}
            {!session.canManage && !session.ended ? <span className={CARD_MUTED}>Viewing only. Changing Agent Policy needs the manage permission.</span> : null}
            {live && step.card !== "choice" ? (
                <Footer>
                    <button type="button" className={`${BTN} ${CARD_PICKED}`} disabled={step.card === "agents" && !AGENT_IDS.some((a) => inScope(session, a))} onClick={() => confirmStep(session.id, stepId)}>Continue</button>
                </Footer>
            ) : null}
        </Card>
    );
}

function ViewCard({ session, index }) {
    const view = session.views[index];
    if (!view || !SOURCES[view.source] || !Array.isArray(view.rows)) return null;
    return (
        <Card label={`Data · ${SOURCES[view.source].title}`}>
            <span className={ROWS}>
                {view.rows.map((r) => (
                    <span key={r.id} className={ROW}>
                        <span className={NAME}><span className={MONO}>{r.name}</span><span className={META}>{rowMeta(r)}</span></span>
                        <span />
                    </span>
                ))}
            </span>
            {!view.rows.length ? <span className={CARD_MUTED}>No rows match.</span> : null}
        </Card>
    );
}

function SettingRow({ pointer, today, after }) {
    return (
        <span className={`${ROW} grid-cols-1`}>
            <span className={NAME}>
                <span className={MONO}>{keyName(pointer)}</span>
                <span className={`${META} break-all`}>{`Today: ${show(today)}`}</span>
                <span className={`${META} break-all text-[color:var(--ink)]`}>{`After: ${after === undefined ? "Removed" : show(after)}`}</span>
            </span>
        </span>
    );
}

function ReviewCard({ session }) {
    const change = changeOf(session);
    const live = session.cur !== "starting" && !session.ended;
    const changed = changedAgents(session);
    const remaining = remainingPilotAgents(session);
    const missing = missingProposalSteps(session);
    const [error, setError] = useState("");
    const ref = useShowWhenLive(live);
    const continueInChat = () => {
        const host = getTriageChatHost();
        if (!host?.say?.(host.current?.(), "Finish preparing the current control proposal using its actual saved draft state. Complete any missing decisions, explain each agent's proposed changes and limitations, and leave the proposal ready for my review. Do not claim a review card or pilot is ready unless the draft state confirms it.")) setError("The conversation is not ready. Reopen it and try again.");
    };
    if (!change) return <Card label="Control proposal" question="No controls have been drafted yet">
        <span className={CARD_MUTED}>The saved draft has no proposed settings. Continue in chat to prepare a recommendation from the investigation.</span>
        {live && session.canManage ? <Button size="sm" onClick={continueInChat}>Continue in chat</Button> : null}
        {error ? <p role="alert">{error}</p> : null}
    </Card>;
    return (
        <Card cardRef={ref} live={live} label="Review" question="Settings this change writes">
            {session.needsReview ? <p className={CARD_MUTED}>{session.needsReview}</p> : null}
            {missing.length ? <span className={CARD_MUTED}>Some recommendations are unfinished. You can pilot the saved settings below or continue preparing them in chat. Recommendations described only in the conversation are not included.</span> : null}
            {AGENT_IDS.filter((a) => change.support[a].level !== "no").map((a) => {
                const rows = changesFor(session, a);
                const doc = session.today?.[a]?.document ?? {};
                if (!inScope(session, a)) return <span key={a} className={CARD_MUTED}>{`${labelOf(a)}: not included.`}</span>;
                return (
                    <span key={a} className="grid gap-[2px]">
                        <span className="text-[12px] font-medium text-[color:var(--ink)]">{`${labelOf(a)} · ${count(rows.length, "setting", "settings")}`}</span>
                        <AgentPilotStatus session={session} agentId={a} />
                        {!rows.length ? <span className={CARD_MUTED}>No change to the current signed policy.</span> : null}
                        <span className={ROWS}>{rows.map((c) => <SettingRow key={c.pointer} pointer={c.pointer} today={valueAt(doc, c.pointer)} after={c.value} />)}</span>
                        {change.effects(session.answers, a, Object.values(session.rows).flat()).map((e) => <span key={e} className={META}>{e}</span>)}
                        {session.today?.[a]?.unsavedEdits ? <span className={CARD_MUTED}>{`The ${labelOf(a)} tab has unsaved edits. This change starts from the current signed policy and does not include them.`}</span> : null}
                    </span>
                );
            })}
            {session.notes.length ? (
                <span className="grid gap-[2px]">
                    <span className={FILE_LABEL}>Notes from the chat</span>
                    {session.notes.map((n) => <span key={`${n.step}:${n.text}`} className={META}>{n.text}</span>)}
                </span>
            ) : null}
            {session.ended && session.cur === "review" ? <Ended /> : null}
            {live ? (
                <Footer hint={session.started?.length ? "Submitted settings stay fixed. Select endpoints for remaining agents." : session.conversational ? "Ask below to revise this proposal." : "To change an answer, ask below."}>
                    <button type="button" className={`${BTN} ${CARD_PICKED}`} disabled={!remaining.length || !session.canManage} onClick={() => toPilot(session.id)}>{remaining.length ? session.started?.length || remaining.length < changed.length ? "Choose remaining pilot endpoints" : "Choose pilot endpoints" : changed.length ? "No remaining pilots to start" : "No changes to pilot"}</button>
                    {missing.length && session.canManage && !session.started?.length ? <Button size="sm" variant="ghost" onClick={continueInChat}>Continue in chat</Button> : null}
                </Footer>
            ) : null}
            {error ? <p role="alert">{error}</p> : null}
        </Card>
    );
}

function AgentPilotStatus({ session, agentId }) {
    const result = pilotResultFor(session, agentId);
    const releaseId = result?.ok ? result.releaseId : session.today[agentId]?.openPilotReleaseId;
    if (releaseId) return <span className={META}>{result?.ok ? "Pilot started." : "Existing pilot."} <Button size="sm" variant="ghost" onClick={() => openAgentPolicyPilot(agentId, releaseId)}>Open {labelOf(agentId)} pilot</Button></span>;
    if (result?.recoveryRequired) return <span className={META}>Pilot outcome not confirmed. <Button size="sm" variant="ghost" onClick={() => askDockTab("investigation")}>Recover saved request in Investigation</Button></span>;
    if (session.today[agentId]?.openPilot) return <span className={META}>Has an open pilot. Finish or abandon it in Agent Policy first.</span>;
    return <span className={META}>{result?.ok ? "Pilot started." : result?.unknown ? "Pilot outcome not confirmed." : "Pilot not started."}</span>;
}

function PilotCard({ session }) {
    const [query, setQuery] = useState("");
    const search = useEndpointSearch(query);
    const live = session.cur === "pilot" && session.canManage && !session.ended;
    const agents = changedAgents(session);
    const remaining = remainingPilotAgents(session);
    const [target, setTarget] = useState("");
    const targetAgent = remaining.includes(target) ? target : remaining[0];
    const selected = session.pilot[targetAgent]?.endpoints ?? [];
    const selectedIds = useMemo(() => new Set(selected.map((e) => e.id)), [selected]);
    const toggle = (endpoint) => {
        const next = selectedIds.has(endpoint.id) ? selected.filter((e) => e.id !== endpoint.id) : [...selected, endpoint].slice(0, PILOT_LIMIT);
        if (targetAgent) setPilot(session.id, targetAgent, { endpoints: next });
    };
    const starting = remaining.filter((a) => session.pilot[a]?.on && session.pilot[a]?.endpoints.length);
    const ref = useShowWhenLive(live);
    const listed = useMemo(() => {
        const byId = new Map(search.endpoints.map((e) => [e.id, e]));
        for (const e of selected) if (!byId.has(e.id)) byId.set(e.id, e);
        return [...byId.values()];
    }, [search.endpoints, selected]);
    return (
        <Card cardRef={ref} live={live} label="Pilot" question="Start a pilot for each agent">
            <span className={CARD_MUTED}>The chosen endpoints receive the change. Every other endpoint keeps today's policy until you sign the release for the tenant.</span>
            <span className={ROWS}>
                {agents.map((a) => {
                    const blocked = pilotAgentLocked(session, a);
                    const on = !!session.pilot[a]?.on && !blocked;
                    return (
                        <button key={a} type="button" role="checkbox" aria-checked={on} disabled={!live || blocked} onClick={() => setPilot(session.id, a, { on: !on })} className={`${ROW} cursor-pointer bg-transparent text-left hover:bg-[color:var(--control-bg)] disabled:cursor-default`}>
                            <span className={NAME}>{labelOf(a)}<span className={META}>{pilotResultFor(session, a)?.ok ? "Pilot already started." : blocked ? "Resolve its existing pilot or saved request first." : count(changesFor(session, a).length, "setting", "settings")}</span></span>
                            {on ? <span aria-hidden="true" className="text-[11px] text-[color:var(--ink)]">{TICK}</span> : <span />}
                        </button>
                    );
                })}
            </span>
            {live && remaining.length ? (
                <span className="grid gap-[6px]" aria-busy={search.loading ? "true" : "false"}>
                    <label className={META}>Choose endpoints for<select className={INPUT} value={targetAgent} onChange={(e) => setTarget(e.target.value)}>{remaining.map((a) => <option key={a} value={a}>{labelOf(a)}</option>)}</select></label>
                    <span className={CARD_MUTED}>Select a separate group for each AI Agent type. The server checks policy support and endpoint eligibility before starting each pilot.</span>
                    <span className={FILE_LABEL}>{`Endpoints · ${selected.length} of at most ${PILOT_LIMIT} selected`}</span>
                    <input className={INPUT} placeholder="Search by hostname" value={query} onChange={(e) => setQuery(e.target.value)} />
                    <span className={`${ROWS} max-h-[260px] overflow-y-auto`}>
                        {listed.map((e) => {
                            const on = selectedIds.has(e.id);
                            return (
                                <button key={e.id} type="button" role="checkbox" aria-checked={on} disabled={!on && selected.length >= PILOT_LIMIT} onClick={() => toggle(e)} className={`${ROW} cursor-pointer bg-transparent text-left hover:bg-[color:var(--control-bg)] disabled:cursor-default ${on ? "!bg-[color:var(--control-bg)]" : ""}`}>
                                    <span className={NAME}>{e.hostname || e.id}<span className={META}>{[e.platform, e.agentVersion ? `Origin Agent ${e.agentVersion}` : ""].filter(Boolean).join(", ")}</span></span>
                                    {on ? <span aria-hidden="true" className="text-[11px] text-[color:var(--ink)]">{TICK}</span> : <span />}
                                </button>
                            );
                        })}
                        {!search.loading && !listed.length ? <span className={CARD_MUTED}>No endpoint matches.</span> : null}
                    </span>
                </span>
            ) : <span className={CARD_MUTED}>{`${count(selected.length, "endpoint", "endpoints")} selected.`}</span>}
            {session.ended && session.cur === "pilot" ? <Ended /> : null}
            {session.ended && session.cur === "starting" ? <span className={CARD_MUTED}>The page reloaded while the pilots were starting. Each agent's tab shows whether its pilot started.</span> : null}
            {!session.ended && (live || session.cur === "starting") ? (
                <Footer hint="">
                    <button type="button" className={`${BTN} ${CARD_PICKED}`} disabled={!live || !starting.length} onClick={() => startPilots(session.id)}>{session.cur === "starting" ? "Starting pilots..." : `Start ${count(starting.length, "pilot", "pilots")}`}</button>
                </Footer>
            ) : null}
        </Card>
    );
}

const NOT_SAVED = "This proposal is not available in this browser. Use Configure controls in its investigation to prepare a new proposal.";

/// The note on the card that was current when the page reloaded.
function Ended() {
    return <span className={CARD_MUTED}>These are historical answers. Continue the local proposal from its investigation, or use Configure controls to prepare a new one.</span>;
}

function DoneCard({ session }) {
    return (
        <Card label="Pilots">
            {!session.started?.length ? <p className={CARD_MUTED}>The pilot launch was interrupted. Check the saved pilot requests in Investigation before trying again.</p> : null}
            {(session.started ?? []).map((r) => (
                <span key={r.agentId} className="text-[12px] leading-[1.4] text-[color:var(--ink)]">
                    {r.ok
                        ? `${labelOf(r.agentId)}: pilot started on ${count(r.endpoints, "endpoint", "endpoints")}. Check its status in Agent Policy.`
                        : `${labelOf(r.agentId)}: ${r.unknown ? "pilot outcome not confirmed." : "pilot not started."} ${r.rejections?.length ? "The server refused the policy." : r.error || "The start failed."}`}
                    {r.warning ? <span className={META}>{r.warning}</span> : null}
                    {r.ok && r.releaseId ? <button type="button" className={`${BTN} mt-[6px]`} onClick={() => openAgentPolicyPilot(r.agentId, r.releaseId)}>Open {labelOf(r.agentId)} pilot</button> : null}
                </span>
            ))}
            {!session.ended && session.started?.some((r) => !r.ok && !r.recoveryRequired) ? <button className={BTN} onClick={() => startPilots(session.id)}>Retry unresolved pilots</button> : null}
            {!session.ended && remainingPilotAgents(session).length ? <Button size="sm" onClick={() => showCurrentProposal(session.id, { review: true, reveal: true })}>Review remaining controls</Button> : null}
        </Card>
    );
}

/// The card a Policy chat job row names.
export default function PolicyChatCard({ cardKey, savedSession, savedView, chatBusy = false }) {
    useEffect(() => { if (savedSession) restoreSession(savedSession); }, [savedSession]);
    const { sessionId, card } = parseCardKey(cardKey);
    const session = useSession(sessionId);
    if (card.startsWith("view:") && savedView) return <ViewCard session={{ views: [savedView] }} index={0} />;
    if (!session) return <Card label="Control proposal"><span className={CARD_MUTED}>{NOT_SAVED}</span></Card>;
    if (card === "active") return <ActiveProposal session={session} chatBusy={chatBusy} />;
    if (card === "pick") return <PickCard session={session} />;
    if (card.startsWith("step:")) {
        const [, stepId, instance] = card.split(":");
        return <StepCard session={session} stepId={stepId} instance={Number(instance)} />;
    }
    if (card.startsWith("view:")) return <ViewCard session={session} index={Number(card.slice(5))} />;
    if (card === "review") return <ReviewCard session={session} />;
    if (card === "pilot") return <PilotCard session={session} />;
    if (card === "done") return <DoneCard session={session} />;
    return null;
}

function ActiveProposal({ session, chatBusy }) {
    const reviewRef = useRef(null);
    useEffect(() => {
        if (!session.reviewOpen) return undefined;
        const frame = requestAnimationFrame(() => reviewRef.current?.scrollIntoView?.({ block: "start" }));
        return () => cancelAnimationFrame(frame);
    }, [session.id, session.reviewOpen]);
    const caps = useMyPermissions();
    const open = useTraceCanvasOpen();
    const canvas = useTraceCanvas(open?.id);
    const current = canvas.investigationId === session.context?.investigationId;
    const writable = !chatBusy && current && !!caps?.available && !caps.subjectScopeDegraded && !session.ended && (!session.guard || session.guard()) && hasPermission(caps, PERMISSIONS.AGENT_CONFIG_MANAGE);
    const visible = writable ? session : { ...session, canManage: false, ended: true };
    const steps = planSteps(session).filter((step) => session.done.includes(step.id));
    if (session.conversational && (chatBusy || (!proposalReviewAvailable(session) && !session.reviewOpen))) return null;
    let card;
    if (session.reviewOpen) card = <ReviewCard session={visible} />;
    else if (session.cur === "done") card = <DoneCard session={visible} />;
    else if (session.cur === "pick") card = <PickCard session={visible} />;
    else if (["pilot", "starting"].includes(session.cur)) card = <PilotCard session={visible} />;
    else if (session.cur === "review" || session.conversational) card = <ReviewCard session={visible} />;
    else card = <StepCard session={visible} stepId={session.cur} instance={session.presented?.[session.cur]} />;
    return <CompactProposal.Provider value>
        <section ref={reviewRef} id={`control-proposal-${session.id}`} aria-label="Control proposal editor" className="grid min-w-0 gap-[8px]" data-control-proposal="">
            {!current ? <p className={CARD_MUTED}>Open this proposal's investigation to continue editing.</p> : null}
            {current && !session.ended && !writable ? <p className={CARD_MUTED}>Editing requires managed configuration manage access and a current local draft.</p> : null}
            {!session.conversational && steps.length ? <details className="border-b border-[color:var(--line-soft)] pb-[8px]">
                <summary className="cursor-pointer text-[12px] text-[color:var(--muted)]">{steps.length} completed {steps.length === 1 ? "step" : "steps"}</summary>
                <div className="mt-[6px] grid gap-[8px]">{steps.map((step) => <div key={step.id} className="flex min-w-0 items-start gap-[8px]">
                    <div className="min-w-0 flex-1 text-[12px]"><p>{step.question}</p><p className="break-words text-[color:var(--muted)]">{summarize(session, step)}</p></div>
                    {writable && !session.started ? <Button size="sm" variant="ghost" onClick={() => reopenStep(session.id, step.id)}>Edit</Button> : null}
                </div>)}</div>
            </details> : null}
            {card}
        </section>
    </CompactProposal.Provider>;
}
